import { Button, Grid, Snackbar, Typography, IconButton } from '@mui/material';
import { Alert } from "@mui/lab";
import React, { useEffect } from 'react';
import { Translate, withLocalize, LocalizeContextProps } from 'react-localize-redux';
import { ButtonPatient } from '../../../../components/general/BedButton';
import { ButtonCancel, ButtonContinue, IconGenerator } from '../../../../components/general/mini_components';
import Modal from '../../../../components/general/modal';
import Loader from '../../../../components/Loader';
import { AgendasTypes, IAppointment, IPatient, OutpatientsVisualizationMode, DEFAULT_CLINIC_TIMEZONE } from '../../../../constants/types';
import { SnackbarType, useSnackBarState } from '../../../../hooks';
import { HOSPITAL_PATIENT } from '../../../../routes/urls';
import { areSameDates, formatBirthdateForDisplay, getPatientID, yearsFromDate, birthdateToDate, fullDateFromPostgresString } from '../../../../utils/index.jsx';
import { RequestStatus } from '../../Request/types';
import { isAwaitingPayment } from '../../Request/utils';
import { PDFDownloadLink } from '@react-pdf/renderer';
import ListPatientsPDFDocument from '../../../../components/pdf/documents/ListPatientsPDFDocument';
import { useSelector } from 'react-redux';


interface AppointmentsDateProps extends LocalizeContextProps {
    appointments: IAppointment[],
    loadingAppointments: boolean,
    loadingSingleAppointment: boolean,
    patientsPersonalData: IPatient[];
    showSnackbar:SnackbarType,
    mode:OutpatientsVisualizationMode,
    dateSelected?: Date;
    callbackSetSnackbar: (showSnackbar:SnackbarType) => void;
    callbackUpdateAppointment: (uuidAppointment: string) => void;
    callbackCancelAppointment: (uuidAppointment: string) => void;
    callbackResetModal: () => void;
    typeAgenda:AgendasTypes;
    timezone?: string;
}

enum ModalAction{
    CHECKIN = "CHECKIN",
    CANCEL = "CANCEL",
    ERROR_CANCEL = "ERROR_CANCEL",
    PRINT_LIST = "PRINT_LIST",
    DELETE = "DELETE"

}
const AppointmentsDateComponent: React.FC<AppointmentsDateProps> = ({ loadingAppointments, loadingSingleAppointment, appointments, mode, showSnackbar, typeAgenda,
    patientsPersonalData, dateSelected, callbackUpdateAppointment, callbackSetSnackbar, callbackCancelAppointment, activeLanguage, translate, timezone }) => {
    const clinicTimezone = timezone || DEFAULT_CLINIC_TIMEZONE;
    const [selectedAppointment, setSelectedAppointment] = React.useState<IAppointment | null>(null);
    const [showModal, setShowModal] = React.useState<{ show: boolean, action?: ModalAction }>({ show: false });
    const billingInfo = useSelector((state: any) => state.investigations.currentInvestigation.billingInfo);
    const agendas = useSelector((state: any) => state.hospital?.data?.agendas || []);
    const currentInvestigation = useSelector((state: any) => state.investigations.currentInvestigation);
    
    // Build PDF config
    const pdfConfig = React.useMemo(() => {
        if (!billingInfo) return null;
        
        // Use active language from withLocalize HOC, fallback to billingInfo or 'fr'
        const locale = activeLanguage?.code || billingInfo.locale || 'fr';
        
        return {
            hospitalName: currentInvestigation?.name || billingInfo.hospitalName,
            logoBlob: billingInfo.logoBlob,
            locale: locale,
            address: billingInfo.address,
            city: billingInfo.city,
            email: billingInfo.email,
            phone: billingInfo.phone,
        };
    }, [billingInfo, currentInvestigation, activeLanguage]);

    useEffect(() => {
        if (showSnackbar.show && showSnackbar.severity === "success") {
            setShowModal({ show: false });
        }
    }, [showSnackbar.show]);

    function resetModal() {
        setShowModal({ show: false });
        setSelectedAppointment(null);
    }

    function confirm() {
        if (selectedAppointment && showModal.action === ModalAction.CHECKIN) {
            callbackUpdateAppointment(selectedAppointment.uuid);
        }
        if (selectedAppointment && showModal.action === ModalAction.CANCEL) {
            callbackCancelAppointment(selectedAppointment.uuid);
        }
        if (selectedAppointment && showModal.action === ModalAction.DELETE) {
            callbackCancelAppointment(selectedAppointment.uuid);
        }
    }

    function renderModal() {
        if(showModal.action === ModalAction.PRINT_LIST){
            if (!pdfConfig) {
                return (
                    <Modal key="modal" open={showModal.show}
                        title={<Translate id={`pages.hospital.outpatients.confirm.${showModal.action}`} />}
                        closeModal={resetModal} >
                        <Typography variant="body1" color="error">
                            <Translate id="general.error" />: Configuration PDF non disponible
                        </Typography>
                    </Modal>
                );
            }
            
            // Build localized filename
            const baseFileName = translate ? 
                translate('pages.hospital.outpatients.patients_list', undefined, { renderInnerHtml: false }) as string : 
                'patients-list';
            const formattedFileName = baseFileName.toLowerCase().replace(/\s+/g, '-');
            const dateStr = dateSelected ? dateSelected.toISOString().split('T')[0] : 'today';
            const fileName = `${formattedFileName}-${dateStr}.pdf`;
            
            return (
                <Modal key="modal" open={showModal.show}
                    title={<Translate id={`pages.hospital.outpatients.confirm.${showModal.action}`} />}
                    closeModal={resetModal} >
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="body2" style={{ marginBottom: '1rem' }}>
                                <Translate id="pages.hospital.outpatients.patients_list" />
                                {dateSelected && ` - ${dateSelected.toLocaleDateString()}`}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <PDFDownloadLink
                                document={
                                    <ListPatientsPDFDocument
                                        appointments={appointments}
                                        patients={patientsPersonalData}
                                        selectedDate={dateSelected}
                                        config={pdfConfig}
                                        agendas={agendas}
                                    />
                                }
                                fileName={fileName}
                                style={{ textDecoration: 'none' }}
                            >
                                {({ loading }) => (
                                    <Button 
                                        variant="contained" 
                                        color="primary" 
                                        disabled={loading}
                                        fullWidth
                                    >
                                        {loading ? <Translate id="general.loading.loading" /> : <Translate id="general.download_pdf" />}
                                    </Button>
                                )}
                            </PDFDownloadLink>
                        </Grid>
                    </Grid>
                </Modal>
            )
        }
        else if (selectedAppointment) {
            const patient = patientsPersonalData.find((pat: IPatient) => pat.uuid === selectedAppointment.patient.uuid);
            if (patient) {
                return (
                    <Modal key="modal" open={showModal.show}
                        title={<Translate id={`pages.hospital.outpatients.confirm.${showModal.action}`} />}
                        closeModal={resetModal} >
                        <Grid container>
                            {
                                (selectedAppointment && showModal.action !== ModalAction.ERROR_CANCEL && showModal.action !== ModalAction.DELETE) &&

                                <Grid item xs={12}>
                                    {

                                        [
                                            <Translate id="investigation.create.personal_data.fields.health_id" />, ":", getPatientID(patient)
                                        ]
                                    }
                                    <Typography variant="body2">
                                        <Translate id="investigation.create.personal_data.fields.name" />: {patient.personalData.name}
                                    </Typography>
                                    <Typography variant="body2">
                                        <Translate id="investigation.create.personal_data.fields.surnames" />: {patient.personalData.surnames}
                                    </Typography>
                                    <Typography variant="body2">
                                        <Translate id="investigation.create.personal_data.fields.sex" />: {patient.personalData.sex === "Male" ? <Translate id="general.male" /> : <Translate id="general.female" />}
                                    </Typography>
                                    <Typography variant="body2">
                                        <Translate id="investigation.create.personal_data.fields.birthdate" />: {formatBirthdateForDisplay(patient.personalData.birthdate, activeLanguage?.code || 'en')}
                                    </Typography>
                                    {
                                        showModal.action === ModalAction.CANCEL &&
                                        <Typography variant="body2">
                                            <Translate id="pages.hospital.outpatients.appointment.date" />: {fullDateFromPostgresString(activeLanguage?.code, selectedAppointment.startDateTime, clinicTimezone)}
                                        </Typography>
                                    }


                                    {
                                        !loadingSingleAppointment &&
                                        <Grid item xs={12} style={{ paddingTop: '1rem' }}>
                                            <ButtonCancel onClick={resetModal} data-testid="cancel-modal" color="primary" spaceright={1}>
                                                <Translate id="general.cancel" />
                                            </ButtonCancel>
                                            &nbsp;
                                            <ButtonContinue onClick={confirm} data-testid="continue-modal" color="primary">
                                                <Translate id="general.continue" />
                                            </ButtonContinue>
                                        </Grid>
                                    }
                                    {
                                        loadingSingleAppointment &&
                                        <Grid item xs={12} style={{ paddingTop: '1rem' }}>
                                            <Loader />
                                        </Grid>
                                    }
                                </Grid>
                            }
                            {
                                (selectedAppointment && showModal.action === ModalAction.ERROR_CANCEL) &&
                                <Typography variant="body2">
                                    <Translate id="pages.hospital.outpatients.error_cancel" />
                                </Typography>
                            }
                            {
                                (selectedAppointment && showModal.action === ModalAction.DELETE) &&
                                <>
                                    <Grid item xs={12}>
                                        <Typography variant="body1" style={{ marginBottom: '1rem' }}>
                                            <Translate id="pages.hospital.outpatients.table_patient_appointments.modal.cancel.message" />
                                        </Typography>
                                        <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                                            <Translate id="investigation.create.personal_data.fields.name" />: {patient.personalData.name} {patient.personalData.surnames}
                                        </Typography>
                                        <Typography variant="body2" style={{ marginBottom: '1rem' }}>
                                            <Translate id="pages.hospital.outpatients.appointment.date" />: {fullDateFromPostgresString(activeLanguage?.code, selectedAppointment.startDateTime, clinicTimezone)}
                                        </Typography>
                                    </Grid>
                                    {
                                        !loadingSingleAppointment &&
                                        <Grid item xs={12} style={{ paddingTop: '1rem' }}>
                                            <ButtonCancel onClick={resetModal} data-testid="cancel-modal" color="primary" spaceright={1}>
                                                <Translate id="general.cancel" />
                                            </ButtonCancel>
                                            &nbsp;
                                            <ButtonContinue onClick={confirm} data-testid="continue-modal" color="primary">
                                                <Translate id="general.continue" />
                                            </ButtonContinue>
                                        </Grid>
                                    }
                                    {
                                        loadingSingleAppointment &&
                                        <Grid item xs={12} style={{ paddingTop: '1rem' }}>
                                            <Loader />
                                        </Grid>
                                    }
                                </>
                            }
                        </Grid>

                    </Modal>
                )
            }
        }

    }
    function handleDeleteAppointment(e: React.MouseEvent, appointment: IAppointment) {
        e.stopPropagation();
        setSelectedAppointment(appointment);
        setShowModal({ show: true, action: ModalAction.DELETE });
    }

    function clickOnAppointment(idAppointment: number) {
        const currentAppointment = appointments.find((app) => app.id === idAppointment);
        if (currentAppointment) {
            if (mode === OutpatientsVisualizationMode.CONSULT) {
                if (currentAppointment.requestAppointment.status === RequestStatus.PENDING_APPROVAL
                    && areSameDates(new Date(currentAppointment.startDateTime), new Date())) {

                    setSelectedAppointment(currentAppointment);
                    setShowModal({ show: true, action: ModalAction.CHECKIN });
                }
                else {
                    // SHER-960: carry the encounter the clinician is opening the record FROM. This
                    // is the only place in the client where an outpatient encounter is
                    // unambiguously identified, and `window.open` starts a new tab with empty
                    // history state — a query parameter is the only thing that survives.
                    const nextUrl = HOSPITAL_PATIENT.replace(":uuidPatient", currentAppointment.patient.uuid)
                        + `?appointment=${currentAppointment.uuid}`;
                    window.open(nextUrl, '_blank');
                }

            }
            else if (mode === OutpatientsVisualizationMode.ADMIN) {
                if (currentAppointment.requestAppointment.status === RequestStatus.PENDING_APPROVAL) {
                    setSelectedAppointment(currentAppointment);
                    setShowModal({ show: true, action: ModalAction.CANCEL });
                }
                else {
                    setSelectedAppointment(currentAppointment);
                    setShowModal({ show: true, action: ModalAction.ERROR_CANCEL });
                }

            }
        }
    }

    if (loadingAppointments) {
        return <Grid style={{ padding: '1rem' }} item xs={12}><Loader /></Grid>
    }
    let appointmentsComponent: JSX.Element[] = [];
    if (appointments.length === 0) {
        appointmentsComponent.push(
            <Grid item xs={12}>
                <Typography variant="h6" style={{ fontWeight: "bold" }} component="h6" align="left">
                    <Translate id="pages.hospital.outpatients.no_appointments" />
                </Typography>
            </Grid>)
    }
    const appointmentsConfirmed = appointments.filter((appointment) => [RequestStatus.ACCEPTED, RequestStatus.COMPLETED].includes(appointment.requestAppointment.status));
    appointmentsConfirmed.sort((appA, appB) => appA.order - appB.order);


    if (appointmentsConfirmed.length > 0) {
        appointmentsComponent = [<>
            <Grid item xs={12}>
                <Typography variant="h6" style={{ fontWeight: "bold" }} component="h6" align="left"><Translate id="pages.hospital.outpatients.checked_in_patients" /></Typography>
            </Grid>
            <Grid container item xs={12}>
                {
                    appointmentsConfirmed.map((appointment, index) => {
                        const patient = patientsPersonalData.find((pat: IPatient) => pat.uuid === appointment.patient.uuid);
                        if (patient) {
                            const patientAge = patient.personalData.birthdate ? yearsFromDate(birthdateToDate(patient.personalData.birthdate)) : undefined;
                            return (
                                <div key={appointment.id} style={{ position: 'relative', display: 'inline-block', marginRight: '1rem', marginBottom: '1rem' }}>
                                    <ButtonPatient patient={patient.personalData}
                                        onSelectPatient={() => clickOnAppointment(appointment.id)}
                                        appointmentStatus={appointment.requestAppointment.status}
                                        age={patientAge} showPersonalData={true}
                                        gender={patient.personalData.sex} />
                                    <IconButton
                                        onClick={(e) => handleDeleteAppointment(e, appointment)}
                                        size="small"
                                        style={{
                                            position: 'absolute',
                                            top: '-8px',
                                            right: '-8px',
                                            backgroundColor: 'white',
                                            padding: '4px',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                        }}
                                    >
                                        <IconGenerator type="delete" />
                                    </IconButton>
                                </div>
                            )
                        }
                    })
                }
            </Grid>
        </>
        ]
    }
    // SHER-203: sort the not-yet-confirmed (booked, awaiting payment) list by scheduled time so the
    // booking sequence is preserved. Confirmed/checked-in patients above are sorted by `order` which
    // is now recalculated server-side at payment-accept time (see bill.controller.ts).
    // Defensive: fall back to 0 if startDateTime is null/undefined/malformed so the comparator
    // never returns NaN (undefined sort behaviour in JS).
    const appointmentsPending = appointments
        .filter((appointment) => appointment.requestAppointment.status === RequestStatus.PENDING_APPROVAL
            || isAwaitingPayment(appointment.requestAppointment.status))
        .sort((appA, appB) => {
            const timeA = appA.startDateTime ? new Date(appA.startDateTime).getTime() || 0 : 0;
            const timeB = appB.startDateTime ? new Date(appB.startDateTime).getTime() || 0 : 0;
            return timeA - timeB;
        });
    if (appointmentsPending.length > 0) {
        appointmentsComponent.push(
            <>
                <Grid item xs={12}>
                    <Typography variant="h6" style={{ fontWeight: "bold" }} component="h6" align="left"><Translate id="pages.hospital.outpatients.not_confirmed_patients" /></Typography>
                </Grid>
                <Grid container item xs={12}>
                    {
                        appointmentsPending.map((appointment, index) => {
                            const patient = patientsPersonalData.find((pat: IPatient) => pat.uuid === appointment.patient.uuid);
                            if (patient) {
                                const patientAge = patient.personalData.birthdate ? yearsFromDate(birthdateToDate(patient.personalData.birthdate)) : undefined;
                                const service = appointment.requestAppointment.service ? appointment.requestAppointment.service.description : '';
                                return (
                                    <div key={appointment.id} style={{ position: 'relative', display: 'inline-block', marginRight: '1rem', marginBottom: '1rem' }}>
                                        <ButtonPatient patient={patient.personalData}
                                            onSelectPatient={() => clickOnAppointment(appointment.id)} 
                                            showPersonalData={true}
                                            service={service}
                                            appointmentStatus={appointment.requestAppointment.status}
                                            age={patientAge} 
                                            gender={patient.personalData.sex} />
                                        <IconButton
                                            onClick={(e) => handleDeleteAppointment(e, appointment)}
                                            size="small"
                                            style={{
                                                position: 'absolute',
                                                top: '-8px',
                                                right: '-8px',
                                                backgroundColor: 'white',
                                                padding: '4px',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                            }}
                                        >
                                            <IconGenerator type="delete" />
                                        </IconButton>
                                    </div>
                                )
                            }

                        })
                    }
                </Grid>
            </>)
    }
    return (
        <>
            <Snackbar
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                open={showSnackbar.show}
                autoHideDuration={2000}
                onClose={() => callbackSetSnackbar({ show: false })}>
                <div>
                    {
                        (showSnackbar.message && showSnackbar.severity) &&
                        <Alert onClose={() => callbackSetSnackbar({ show: false })} severity={showSnackbar.severity}>
                            <Translate id={showSnackbar.message} />
                        </Alert>
                    }
                </div>
            </Snackbar>
            {
                appointments.length > 0 &&
                <Button onClick={() => setShowModal({action: ModalAction.PRINT_LIST, show:true})}><Translate id="pages.hospital.outpatients.print_list" /></Button>
            }
            {renderModal()}
            <Grid container spacing={2} style={{ padding: '1rem' }}>
                {appointmentsComponent}
            </Grid>
        </>

    );
}

// Wrap with withLocalize to get activeLanguage prop
export const AppointmentsDate = withLocalize(AppointmentsDateComponent);